import type {
	ChangeEventHandler,
	FC,
	FormEvent,
	InputHTMLAttributes,
} from "react";
import Button from "../../../components/general/Button";
import Icon from "../../../components/general/Icon";
import ErrorMessage from "../ErrorMessage";
import { cn } from "@babybjorn/ui";
import * as s from "./SingleInputForm.module.scss";

type SingleInputFormProps = InputHTMLAttributes<HTMLInputElement> & {
	id: string | null;
	buttonLabel?: string;
	valid?: boolean | null;
	loading?: boolean;
	input: string;
	errorMessage?: string;
	successMessage?: string;
	appliedDiscountCode?: string;
	onFormSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
	onInputChange: ChangeEventHandler<HTMLInputElement>;
	removeDiscountCode?: () => Promise<void>;
	negativeDiscount?: string;
	removeButtonLabel: string;
};

const SingleInputForm: FC<SingleInputFormProps> = ({
	id,
	buttonLabel,
	input,
	loading,
	errorMessage,
	successMessage,
	appliedDiscountCode,
	onFormSubmit,
	onInputChange,
	removeDiscountCode,
	negativeDiscount,
	removeButtonLabel,
	...rest
}) => {
	const emptyInput = input === "";
	const editInput = input !== "";
	const errorMode = errorMessage;
	const successMode = successMessage;

	const InputContainerClassNames = cn(s.inputFieldContainer, {
		[s.errorMode]: errorMode,
	});

	const InputIcon = loading ? (
		<Button
			icon="reload"
			loading
			variant="primary"
			buttonType="input"
			className={cn(s.loadIcon)}
			aria-label={buttonLabel ?? "input-button"}
			role="button"
		/>
	) : (
		<Icon
			icon={successMode ? "checkmark" : "close"}
			className={cn(s.resultIcon, { [s.errorMode]: errorMode })}
		/>
	);

	const onSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		void onFormSubmit(event);
	};

	return (
		<form className={s.form} onSubmit={onSubmit}>
			<div className={cn(s.formWrapper)}>
				<div className={InputContainerClassNames}>
					<input
						type="text"
						id={id}
						className={cn(s.input)}
						value={input}
						onChange={onInputChange}
						{...rest}
					/>
					{!appliedDiscountCode && loading === false ? (
						<Button
							icon={emptyInput ? "arrow-right" : undefined}
							loading={loading}
							type={emptyInput ? "button" : "submit"}
							variant={emptyInput ? "secondary" : "primary"}
							label={editInput ? buttonLabel : undefined}
							buttonType={emptyInput ? "input" : "standard"}
							className={cn(s.inputButton)}
							aria-label={buttonLabel ?? "input-button"}
						/>
					) : (
						InputIcon
					)}
				</div>
				{errorMode ? (
					<ErrorMessage message={errorMessage} />
				) : (
					<div className={s.feedbackContainer}>
						<p>
							{appliedDiscountCode
								? `${negativeDiscount} ${successMessage}`
								: (successMessage ?? "")}
						</p>
						{appliedDiscountCode && removeDiscountCode && (
							<Button
								label={removeButtonLabel}
								onClick={() => void removeDiscountCode()}
								alwaysSmall
								variant="secondary"
							/>
						)}
					</div>
				)}
			</div>
		</form>
	);
};

export default SingleInputForm;
